import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { displayLabels, saveStatus, splitName, type DocMeta } from '../core/documents';
import { isUnprotected } from '../core/cloud';
import { useApp, useCloudAuth, useCloudSummary, useSession, useUi, useWindow, useWindowId } from '../app/context';
import { openAccount, patchUi, setRendered } from '../app/ui';
import { savePreference, type ThemePreference } from '../platform/theme';
import { play, setGesture, SPRING_UI } from '../motion/motion';
import { CloseIcon, CloudIcon, CurrentLineIcon, EyeIcon, GearIcon, LayoutIcon, LineNumbersIcon, MarkdownIcon, MoonIcon, OpenIcon, PlusIcon, SourceIcon, StackIcon, SunIcon, SystemIcon, TabKeyIcon, TextIcon, WindowIcon, WorkspacesIcon } from './icons';
import { iconGesture } from './iconMotion';
import { shortcutHint } from './keys';
import { MenuItem, Popover, menuKeys } from './Popover';
import { DocumentCard } from './DocumentCard';
import { WindowControls } from './WindowControls';
import { useWindowGestures } from './Window';
import { SyncIndicator } from './SyncIndicator';
import { WorkspaceKey } from './Workspaces';

/**
 * The head of a window. Its one job is "which document": a quiet strip of
 * names in which the active document *is* the title. The window's own
 * controls sit at its left edge; any free space in the head carries the
 * window when dragged, and a double-click maximises it.
 */
export function Head() {
  const app = useApp();
  const windowId = useWindowId();
  const docIds = useWindow((w) => w.docIds);
  const allDocs = useSession((s) => s.docs);
  const list = useMemo(() => {
    const byId = new Map(allDocs.map((d) => [d.id, d]));
    return docIds.map((id) => byId.get(id)).filter((d): d is DocMeta => !!d);
  }, [docIds, allDocs]);
  const activeId = useWindow((w) => w.activeId);
  const compact = useUi((s) => s.compact);
  const { startMove } = useWindowGestures();
  const [menu, setMenu] = useState<null | 'new' | 'view' | 'all' | 'doc'>(null);
  const [overflow, setOverflow] = useState(false);
  const [newEl, setNewEl] = useState<HTMLButtonElement | null>(null);
  const [viewEl, setViewEl] = useState<HTMLButtonElement | null>(null);
  const [allEl, setAllEl] = useState<HTMLButtonElement | null>(null);
  const [docAnchor, setDocAnchor] = useState<HTMLElement | null>(null);

  const openDoc = (anchor: HTMLElement) => {
    setDocAnchor(anchor);
    setMenu('doc');
    iconGesture(anchor.querySelector('.tab-more'), 'gear');
  };

  // Switching away from a document, or closing it, retires its card.
  const [prevActive, setPrevActive] = useState(activeId);
  if (activeId !== prevActive) {
    setPrevActive(activeId);
    if (menu === 'doc') setMenu(null);
  }

  // A card can also be asked for from elsewhere (a tab's context menu, the palette).
  useEffect(() => {
    const open = (e: Event) => {
      const detail = (e as CustomEvent<{ windowId: string }>).detail;
      if (detail.windowId !== windowId) return;
      const tab = document.querySelector<HTMLElement>(`[data-window="${windowId}"] .tab-main[data-active="true"]`);
      if (tab) openDoc(tab);
    };
    window.addEventListener('mytxt:document-card', open);
    return () => window.removeEventListener('mytxt:document-card', open);
  }, [windowId]);

  /** Free space in the head moves the window. */
  const isBackground = (target: EventTarget) => target instanceof HTMLElement && !!target.closest('[data-drag-region]') && !target.closest('button, .tab, input');

  return (
    <header
      className="head"
      data-window={windowId}
      data-empty={list.length === 0 || undefined}
      data-drag-region
      onPointerDown={(e) => isBackground(e.target) && startMove(e)}
      onDoubleClick={(e) => isBackground(e.target) && !compact && app.toggleMaximize(windowId)}
    >
      {!compact && <WindowControls />}
      {compact && <WorkspaceKey windowId={windowId} />}
      <Tabs docs={list} activeId={activeId} onOpenCard={openDoc} onOverflow={setOverflow} />
      <div className="head-actions">
        <SyncIndicator />
        {overflow && (
          <button
            ref={setAllEl}
            type="button"
            className="icon-btn all-btn"
            aria-label={`All documents (${list.length})`}
            aria-haspopup="menu"
            aria-expanded={menu === 'all'}
            data-tip="All documents"
            data-tip-side="bottom"
            onClick={() => setMenu(menu === 'all' ? null : 'all')}
          >
            <StackIcon size={16} />
            <span className="all-count">{list.length}</span>
          </button>
        )}
        <button
          ref={setNewEl}
          type="button"
          className="icon-btn new-btn"
          aria-label="New or open"
          aria-haspopup="menu"
          aria-expanded={menu === 'new'}
          data-tip="New or open"
          data-tip-side="bottom"
          onClick={() => setMenu(menu === 'new' ? null : 'new')}
        >
          <PlusIcon size={17} />
        </button>
        <ViewButton buttonRef={setViewEl} open={menu === 'view'} onToggle={() => setMenu(menu === 'view' ? null : 'view')} />
      </div>

      <NewMenu open={menu === 'new'} anchor={newEl} onClose={() => setMenu(null)} />
      <ViewMenu open={menu === 'view'} anchor={viewEl} onClose={() => setMenu(null)} />
      <AllMenu open={menu === 'all'} anchor={allEl} docs={list} activeId={activeId} onClose={() => setMenu(null)} />
      <Popover open={menu === 'doc' && !!activeId} anchor={docAnchor} onClose={() => setMenu(null)} label="Document" className="doc-popover">
        {activeId && <DocumentCard id={activeId} onDone={() => setMenu(null)} />}
      </Popover>
    </header>
  );
}

// ---------------------------------------------------------------------------

interface Leaving {
  doc: DocMeta;
  index: number;
}

/** Capture where each tab is drawn now, to animate it from there after the order changes. */
function snapshot(listEl: HTMLElement | null): Map<string, number> {
  const map = new Map<string, number>();
  listEl?.querySelectorAll<HTMLElement>('.tab:not([data-leaving])').forEach((el) => map.set(el.dataset.doc!, el.getBoundingClientRect().left));
  return map;
}

function runFlip(listEl: HTMLElement | null, from: Map<string, number>) {
  listEl?.querySelectorAll<HTMLElement>('.tab:not([data-leaving])').forEach((el) => {
    const before = from.get(el.dataset.doc!);
    if (before === undefined) return;
    const delta = before - el.getBoundingClientRect().left;
    if (Math.abs(delta) < 0.5) return;
    play(el, [{ transform: `translateX(${delta}px)` }, { transform: 'translateX(0px)' }], { duration: SPRING_UI.ms, easing: SPRING_UI.css }).then(() => (el.style.transform = ''));
  });
}

function Tabs({ docs, activeId, onOpenCard, onOverflow }: { docs: DocMeta[]; activeId: string | null; onOpenCard: (el: HTMLElement) => void; onOverflow: (o: boolean) => void }) {
  const app = useApp();
  const viewport = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const indicator = useRef<HTMLDivElement>(null);
  const flipFrom = useRef<Map<string, number> | null>(null);
  const suppressClick = useRef(false);
  const labels = displayLabels(docs);
  const order = docs.map((d) => d.id).join('|');

  // Keep closed tabs briefly so they can fold away instead of vanishing.
  const [prevDocs, setPrevDocs] = useState(docs);
  const [leaving, setLeaving] = useState<Leaving[]>([]);
  if (docs !== prevDocs) {
    const ids = new Set(docs.map((d) => d.id));
    const removed = prevDocs.flatMap((doc, index) => (ids.has(doc.id) ? [] : [{ doc, index }]));
    setPrevDocs(docs);
    if (removed.length) setLeaving((l) => [...l.filter((x) => !ids.has(x.doc.id)), ...removed]);
  }
  useEffect(() => {
    if (!leaving.length) return;
    const t = setTimeout(() => setLeaving([]), 220);
    return () => clearTimeout(t);
  }, [leaving]);

  const rendered: Array<{ doc: DocMeta; leaving: boolean }> = docs.map((doc) => ({ doc, leaving: false }));
  for (const l of [...leaving].sort((a, b) => a.index - b.index)) rendered.splice(Math.min(l.index, rendered.length), 0, { doc: l.doc, leaving: true });

  // After a reorder, tabs travel from where they were drawn to their new places.
  useLayoutEffect(() => {
    if (!flipFrom.current) return;
    runFlip(list.current, flipFrom.current);
    flipFrom.current = null;
  }, [order]);

  // The active indicator glides between tabs and follows them as they resize.
  useLayoutEffect(() => {
    const listEl = list.current;
    const ind = indicator.current;
    if (!listEl || !ind) return;
    let first = !ind.dataset.ready;
    const place = () => {
      const el = listEl.querySelector<HTMLElement>('.tab[data-active="true"]');
      if (!el) {
        ind.style.opacity = '0';
        return;
      }
      ind.style.opacity = '1';
      ind.style.transform = `translateX(${el.offsetLeft}px)`;
      ind.style.width = `${el.offsetWidth}px`;
      if (first) {
        first = false;
        requestAnimationFrame(() => (ind.dataset.ready = 'true'));
      }
    };
    place();
    const ro = new ResizeObserver(place);
    listEl.querySelectorAll('.tab').forEach((t) => ro.observe(t));
    ro.observe(listEl);
    return () => ro.disconnect();
  });

  // Overflow, edge fades, and keeping the active tab in view.
  useLayoutEffect(() => {
    const vp = viewport.current;
    const listEl = list.current;
    if (!vp || !listEl) return;
    const update = () => {
      const over = listEl.scrollWidth > vp.clientWidth + 1;
      onOverflow(over);
      vp.dataset.fadeStart = String(over && vp.scrollLeft > 2);
      vp.dataset.fadeEnd = String(over && vp.scrollLeft < listEl.scrollWidth - vp.clientWidth - 2);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(vp);
    ro.observe(listEl);
    vp.addEventListener('scroll', update, { passive: true });
    return () => {
      ro.disconnect();
      vp.removeEventListener('scroll', update);
    };
  }, [onOverflow]);

  useEffect(() => {
    const reveal = () => {
      const vp = viewport.current;
      const el = vp?.querySelector<HTMLElement>('.tab[data-active="true"]');
      if (!vp || !el || list.current?.dataset.reordering) return;
      const pad = 32;
      const left = el.offsetLeft - pad;
      const right = el.offsetLeft + el.offsetWidth + pad - vp.clientWidth;
      const target = left < vp.scrollLeft ? left : right > vp.scrollLeft ? right : null;
      if (target !== null) {
        const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
        vp.scrollTo({ left: Math.max(0, target), behavior: smooth ? 'smooth' : 'auto' });
      }
    };
    reveal();
    // A tab that is still unfolding has not reached its final width yet; look again once it has.
    const t = setTimeout(reveal, 300);
    return () => clearTimeout(t);
  }, [activeId, docs.length]);

  const moveTab = (id: string, index: number) => {
    flipFrom.current = snapshot(list.current);
    app.reorder(id, index);
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const tabs = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
    const index = tabs.indexOf(document.activeElement as HTMLButtonElement);
    if (index < 0) return;
    const id = tabs[index].dataset.id!;
    if (e.altKey && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
      // Alt+arrows carry the focused tab along the strip.
      e.preventDefault();
      moveTab(id, index + (e.key === 'ArrowRight' ? 1 : -1));
      return;
    }
    let next = -1;
    if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      app.close(id).then(() => list.current?.querySelector<HTMLElement>('[role="tab"][tabindex="0"]')?.focus());
      return;
    } else if (e.key === 'F2') {
      e.preventDefault();
      onOpenCard(tabs[index]);
      return;
    } else if (e.key === 'ArrowDown' || e.key === 'Escape') {
      e.preventDefault();
      app.focusEditor();
      return;
    }
    if (next >= 0) {
      e.preventDefault();
      tabs[next].focus();
      app.activate(tabs[next].dataset.id!, { focus: false });
    }
  };

  const onWheel = (e: React.WheelEvent) => {
    const vp = viewport.current;
    if (vp && Math.abs(e.deltaY) > Math.abs(e.deltaX)) vp.scrollLeft += e.deltaY;
  };

  /**
   * Drag to reorder. The picked-up tab lifts and follows the pointer along the
   * strip; its neighbours part to show where it will land; near an edge the
   * strip scrolls. On release every tab travels to its new place.
   */
  const onTabPointerDown = (doc: DocMeta) => (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('.tab-close')) return;
    const vp = viewport.current;
    const listEl = list.current;
    const tabEl = e.currentTarget;
    if (!vp || !listEl) return;
    const x0 = e.clientX;
    const y0 = e.clientY;
    const pointerId = e.pointerId;
    let started = false;
    let pointerX = x0;
    let frame = 0;
    let target = 0;
    let from = 0;
    let scroll0 = 0;
    let tabs: Array<{ id: string; el: HTMLElement; left: number; width: number }> = [];

    const layout = () => {
      const dragged = tabs[from];
      const listWidth = listEl.scrollWidth;
      const dx = Math.min(listWidth - dragged.left - dragged.width - 2, Math.max(-dragged.left + 2, pointerX - x0 + (vp.scrollLeft - scroll0)));
      dragged.el.style.transform = `translateX(${dx}px)`;
      const centre = dragged.left + dragged.width / 2 + dx;
      target = tabs.filter((t, i) => i !== from && t.left + t.width / 2 < centre).length;
      const shift = dragged.width + 2;
      tabs.forEach((t, i) => {
        if (i === from) return;
        let offset = 0;
        if (from < target && i > from && i <= target) offset = -shift;
        if (target < from && i >= target && i < from) offset = shift;
        t.el.style.transform = offset ? `translateX(${offset}px)` : '';
      });
    };

    const autoScroll = () => {
      const r = vp.getBoundingClientRect();
      const edge = 40;
      const speed = pointerX < r.left + edge ? -Math.ceil((r.left + edge - pointerX) / 4) : pointerX > r.right - edge ? Math.ceil((pointerX - (r.right - edge)) / 4) : 0;
      if (speed) {
        vp.scrollLeft += speed;
        layout();
      }
      frame = requestAnimationFrame(autoScroll);
    };

    const begin = () => {
      started = true;
      tabs = Array.from(listEl.querySelectorAll<HTMLElement>('.tab:not([data-leaving])')).map((el) => ({ id: el.dataset.doc!, el, left: el.offsetLeft, width: el.offsetWidth }));
      from = tabs.findIndex((t) => t.id === doc.id);
      target = from;
      scroll0 = vp.scrollLeft;
      tabEl.setPointerCapture?.(pointerId);
      tabEl.dataset.lifted = 'true';
      listEl.dataset.reordering = 'true';
      setGesture('tab');
      frame = requestAnimationFrame(autoScroll);
    };

    const move = (ev: PointerEvent) => {
      pointerX = ev.clientX;
      if (!started) {
        if (Math.abs(ev.clientX - x0) < 5 || Math.abs(ev.clientX - x0) < Math.abs(ev.clientY - y0)) return;
        begin();
      }
      layout();
    };

    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      cancelAnimationFrame(frame);
      if (!started) return;
      suppressClick.current = true;
      setTimeout(() => (suppressClick.current = false), 0);
      const before = snapshot(listEl);
      tabs.forEach((t) => (t.el.style.transform = ''));
      delete tabEl.dataset.lifted;
      delete listEl.dataset.reordering;
      setGesture(null);
      if (target !== from) {
        flipFrom.current = before;
        app.reorder(doc.id, target);
      } else {
        runFlip(listEl, before);
      }
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  return (
    <div className="tabs-viewport" ref={viewport} onWheel={onWheel} data-drag-region>
      <div className="tabs" role="tablist" aria-label="Open documents" ref={list} onKeyDown={onKey} data-drag-region>
        <div className="tab-indicator" ref={indicator} aria-hidden="true" />
        {rendered.map(({ doc, leaving: isLeaving }) => (
          <Tab
            key={doc.id}
            doc={doc}
            active={doc.id === activeId}
            leaving={isLeaving}
            ordinal={labels.get(doc.id) ?? null}
            onOpenCard={onOpenCard}
            onPointerDown={onTabPointerDown(doc)}
            suppressClick={suppressClick}
          />
        ))}
        <div className="head-drag" data-drag-region aria-hidden="true" />
      </div>
    </div>
  );
}

const STATUS_WORDS: Record<string, string> = {
  draft: 'not saved to a file',
  edited: 'unsaved changes',
  'cloud-pending': 'waiting to sync to the cloud',
};

interface TabProps {
  doc: DocMeta;
  active: boolean;
  leaving: boolean;
  ordinal: string | null;
  onOpenCard: (el: HTMLElement) => void;
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  suppressClick: React.RefObject<boolean>;
}

function Tab({ doc, active, leaving, ordinal, onOpenCard, onPointerDown, suppressClick }: TabProps) {
  const app = useApp();
  const { base, ext } = splitName(doc.name);
  const status = saveStatus(doc);
  const unsaved = STATUS_WORDS[status];

  const click = (e: MouseEvent<HTMLButtonElement>) => {
    if (suppressClick.current) return;
    if (active) onOpenCard(e.currentTarget);
    else app.activate(doc.id);
  };

  return (
    <div
      className="tab"
      data-doc={doc.id}
      data-active={active}
      data-dirty={isUnprotected(doc) || undefined}
      data-leaving={leaving || undefined}
      inert={leaving || undefined}
      onPointerDown={onPointerDown}
      onContextMenu={(e) => {
        e.preventDefault();
        patchUi({ contextMenu: { x: e.clientX, y: e.clientY, target: { kind: 'tab', docId: doc.id }, returnFocus: e.currentTarget.querySelector('button') } });
      }}
    >
      <button
        type="button"
        role="tab"
        className="tab-main"
        data-id={doc.id}
        data-active={active}
        aria-selected={active}
        aria-label={`${doc.name}${ordinal ? ` (${ordinal})` : ''}${unsaved ? `, ${unsaved}` : ''}`}
        aria-description="Alt and arrow keys move this tab"
        aria-haspopup={active ? 'dialog' : undefined}
        tabIndex={active ? 0 : -1}
        data-tip={active ? `${doc.name} — document options` : doc.name.length > 22 ? doc.name : undefined}
        data-tip-side="bottom"
        onClick={click}
        onAuxClick={(e) => e.button === 1 && app.close(doc.id)}
      >
        <span className="tab-base">{base}</span>
        <span className="tab-ext">{ext}</span>
        {ordinal && <span className="tab-ordinal">{ordinal}</span>}
        <span className="tab-more" aria-hidden="true">
          <GearIcon size={12} />
        </span>
      </button>
      <button type="button" className="tab-close" tabIndex={-1} aria-label={`Close ${doc.name}`} onMouseDown={(e) => e.preventDefault()} onClick={() => app.close(doc.id)}>
        <span className="tab-dot" aria-hidden="true" />
        <CloseIcon size={13} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

function NewMenu({ open, anchor, onClose }: { open: boolean; anchor: HTMLElement | null; onClose: () => void }) {
  const app = useApp();
  const windowId = useWindowId();
  const compact = useUi((s) => s.compact);
  const pick = (fn: () => void) => () => {
    onClose();
    fn();
  };
  return (
    <Popover open={open} anchor={anchor} placement="below-end" onClose={onClose} label="New or open" role="menu">
      <div className="menu" onKeyDown={menuKeys}>
        <MenuItem icon={<MarkdownIcon size={17} />} label="New Markdown" hint={shortcutHint('Mod-n')} onSelect={pick(() => app.newDocument('md', '', windowId))} />
        <MenuItem icon={<TextIcon size={17} />} label="New Plain Text" hint={shortcutHint('Mod-Alt-n')} onSelect={pick(() => app.newDocument('txt', '', windowId))} />
        <MenuItem icon={<OpenIcon size={17} />} label="Open File" hint={shortcutHint('Mod-o')} onSelect={pick(() => app.openFiles(windowId))} />
        <div className="menu-sep" role="separator" />
        {compact ? (
          <MenuItem icon={<WorkspacesIcon size={17} />} label="New Workspace" onSelect={pick(() => app.newWindow())} />
        ) : (
          <MenuItem icon={<WindowIcon size={17} />} label="New Window" onSelect={pick(() => app.newWindow())} />
        )}
      </div>
    </Popover>
  );
}

const THEMES: Array<{ id: ThemePreference; label: string; icon: typeof SunIcon }> = [
  { id: 'system', label: 'Match System', icon: SystemIcon },
  { id: 'light', label: 'Light', icon: SunIcon },
  { id: 'dark', label: 'Dark', icon: MoonIcon },
];

export function chooseTheme(id: ThemePreference) {
  savePreference(id);
  patchUi({ theme: id });
}

/** Settings. The cloud's state is not repeated here: the head's cloud glyph carries it whenever there is one. */
function ViewButton({ buttonRef, open, onToggle }: { buttonRef: (el: HTMLButtonElement | null) => void; open: boolean; onToggle: () => void }) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className="icon-btn settings-btn"
      aria-label="Settings"
      aria-haspopup="menu"
      aria-expanded={open}
      data-tip="Settings"
      data-tip-side="bottom"
      onClick={(e) => {
        if (!open) iconGesture(e.currentTarget, 'gear');
        onToggle();
      }}
    >
      <GearIcon size={16} />
    </button>
  );
}

/**
 * Settings: the whole preferences surface, small enough to be a menu —
 * appearance, three editor switches, how a Markdown document is shown, and
 * the way into window layouts.
 */
function ViewMenu({ open, anchor, onClose }: { open: boolean; anchor: HTMLElement | null; onClose: () => void }) {
  const app = useApp();
  const windowId = useWindowId();
  const theme = useUi((s) => s.theme);
  const prefs = useUi((s) => s.prefs);
  const compact = useUi((s) => s.compact);
  const activeId = useWindow((w) => w.activeId);
  const md = useSession((s) => s.docs.find((d) => d.id === activeId)?.kind === 'md');
  const rendered = useUi((s) => !!activeId && s.rendered.has(activeId));
  return (
    <Popover open={open} anchor={anchor} placement="below-end" onClose={onClose} label="Settings" role="menu" className="settings-popover">
      <div className="menu" onKeyDown={menuKeys}>
        <div className="menu-heading" aria-hidden="true">
          Appearance
        </div>
        <div className="menu-segmented" role="group" aria-label="Appearance">
          {THEMES.map((t) => (
            <button key={t.id} type="button" role="menuitemradio" aria-checked={theme === t.id} aria-label={t.label} className="menu-segment" tabIndex={-1} onClick={() => chooseTheme(t.id)}>
              <t.icon size={15} />
              <span>{t.id === 'system' ? 'Auto' : t.label}</span>
            </button>
          ))}
        </div>
        <div className="menu-sep" role="separator" />
        <MenuItem icon={<LineNumbersIcon size={16} />} label="Line Numbers" toggle checked={prefs.lineNumbers} onSelect={() => app.setPrefs({ lineNumbers: !prefs.lineNumbers })} />
        <MenuItem icon={<CurrentLineIcon size={16} />} label="Highlight Current Line" toggle checked={prefs.currentLine} onSelect={() => app.setPrefs({ currentLine: !prefs.currentLine })} />
        <MenuItem icon={<TabKeyIcon size={16} />} label="Tab Indents Selection" toggle checked={prefs.tabIndent} onSelect={() => app.setPrefs({ tabIndent: !prefs.tabIndent })} />
        {md && activeId && (
          <>
            <div className="menu-sep" role="separator" />
            <div className="menu-heading" aria-hidden="true">
              Markdown
            </div>
            <div className="menu-segmented" role="group" aria-label="Show Markdown as">
              <button type="button" role="menuitemradio" aria-checked={!rendered} className="menu-segment" tabIndex={-1} onClick={() => setRendered(activeId, false)}>
                <SourceIcon size={15} />
                <span>Source</span>
              </button>
              <button type="button" role="menuitemradio" aria-checked={rendered} className="menu-segment" tabIndex={-1} onClick={() => setRendered(activeId, true)}>
                <EyeIcon size={15} />
                <span>Rendered</span>
              </button>
            </div>
          </>
        )}
        {app.cloud.configured && (
          <>
            <div className="menu-sep" role="separator" />
            <MenuItem
              icon={<CloudIcon size={16} />}
              label={<CloudMenuLabel />}
              onSelect={() => {
                onClose();
                openAccount();
              }}
            />
          </>
        )}
        {!compact && (
          <>
            <div className="menu-sep" role="separator" />
            <MenuItem
              icon={<LayoutIcon size={16} />}
              label="Window Layout…"
              onSelect={() => {
                onClose();
                patchUi({ layoutFor: windowId });
              }}
            />
          </>
        )}
      </div>
    </Popover>
  );
}

/** "Account & Cloud…" with its state in words, so the Settings menu doubles as the status line. */
function CloudMenuLabel() {
  const summary = useCloudSummary();
  const auth = useCloudAuth();
  return (
    <>
      <span>{auth.status === 'signed-in' ? 'Account & Cloud…' : 'Sign In…'}</span>
      <span className="menu-sub" data-tone={summary.tone}>
        {summary.label}
      </span>
    </>
  );
}

function AllMenu({ open, anchor, docs, activeId, onClose }: { open: boolean; anchor: HTMLElement | null; docs: DocMeta[]; activeId: string | null; onClose: () => void }) {
  const app = useApp();
  const labels = displayLabels(docs);
  return (
    <Popover open={open} anchor={anchor} placement="below-end" onClose={onClose} label="All documents" role="menu" className="all-popover">
      <div className="menu" onKeyDown={menuKeys}>
        {docs.map((d) => {
          const { base, ext } = splitName(d.name);
          return (
            <MenuItem
              key={d.id}
              className="doc-item"
              icon={<span className="doc-item-dot" data-dirty={isUnprotected(d) || undefined} aria-label={isUnprotected(d) ? 'unsaved' : undefined} />}
              label={
                <>
                  <span className="doc-item-base">{base}</span>
                  <span className="tab-ext">{ext}</span>
                  {labels.get(d.id) && <span className="tab-ordinal">{labels.get(d.id)}</span>}
                </>
              }
              checked={d.id === activeId}
              onSelect={() => {
                onClose();
                app.activate(d.id);
              }}
            />
          );
        })}
      </div>
    </Popover>
  );
}
